import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Chip, styled, Card, CardContent, Grid, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { formatDateToDDMMYYYY } from '../utils/dateUtils';
import { BRAND_TABLE_TEXT_COLOR } from '../styles/tableColors';

const fieldMeta = {
  amountDueTillCurrent: { label: 'Next Payment', infoText: 'Next Payment Amount as per the Contract' },
  nextInstallmentDate: { label: 'Due on', infoText: 'Date of the next Due' },
  numberOfInstallments: { label: 'Term', infoText: 'Total Loan duration' },
  remainingTermMonths: { label: 'Remaining Term', infoText: 'Remaining term in months' },
};

const formatCurrency = (value?: string) =>
  value ? `£${Number(value).toLocaleString('en-GB', { minimumFractionDigits: 2 })}` : '-';

const LoanId = styled(Typography)(({ theme }) => ({
  color: BRAND_TABLE_TEXT_COLOR,
  fontFamily: "'Libre Franklin', sans-serif",
  fontWeight: 400,
  fontSize: 18,
  marginRight: theme.spacing(1),
}));

const AccountName = styled(Typography)(({ theme }) => ({
  color: BRAND_TABLE_TEXT_COLOR,
  fontFamily: "'Libre Franklin', sans-serif",
  fontWeight: 400,
  fontSize: 18,
  marginRight: theme.spacing(1),
}));

const LoanAmount = styled(Typography)(({ theme }) => ({
  color: BRAND_TABLE_TEXT_COLOR,
  fontFamily: "'Libre Franklin', sans-serif",
  fontWeight: 400,
  fontSize: 24,
  margin: theme.spacing(1, 0),
}));

const LoanStatusChip = styled(Chip)<{ status?: string }>(({ theme, status }) => {
  const lowerCaseStatus = status?.toLowerCase() || '';
  let bg, color;
  if (lowerCaseStatus.includes('bad')) {
    bg = theme.palette.mode === 'dark' ? '#3a2323' : '#fbeae5';
    color = theme.palette.mode === 'dark' ? '#ff4d4f' : '#d9452b';
  } else if (lowerCaseStatus.includes('active')) {
    bg = theme.palette.mode === 'dark' ? '#173F2C' : '#e6f4ea';
    color = theme.palette.mode === 'dark' ? '#3DF06A' : '#1e8e3e';
  } else {
    bg = theme.palette.mode === 'dark' ? '#222' : '#f4f6f9';
    color = theme.palette.mode === 'dark' ? '#888' : '#5a6470';
  }
  return {
    background: bg,
    color,
    fontFamily: "'Libre Franklin', sans-serif",
    fontWeight: 400,
    fontSize: 16,
    borderRadius: 15,
    padding: '2px 18px',
    marginLeft: theme.spacing(2),
    height: 32,
    letterSpacing: '0.5px',
    display: 'flex',
    alignItems: 'center',
    '.MuiChip-label': {
      padding: 0,
    },
  };
});

const ViewDetailsButton = styled(Button)(({ theme }) => ({
  border: '1.5px solid #98773b',
  color: '#98773b',
  background: 'transparent',
  fontFamily: "'Libre Franklin', sans-serif",
  fontWeight: 500,
  fontSize: 14,
  borderRadius: 8,
  padding: theme.spacing(1, 4),
  marginLeft: theme.spacing(4),
  boxShadow: 'none',
  '&:hover': {
    background: theme.palette.action.hover,
    boxShadow: 'none',
    border: '1.5px solid #04173f',
  },
}));

interface LoanCardProps {
  loan: {
    id: string;
    name: string;
    productName?: string;
    loanProductName?: string;
    accountName?: string;
    status?: string;
    loanAmount?: string;
    principalRemaining?: string;
    amountDueTillCurrent?: string;
    nextInstallmentDate?: string;
    numberOfInstallments?: string;
    remainingTermMonths?: string;
    loanAgreementNumber?: string;
    restrictAccessToPortal?: boolean;
  };
}

const LoanCard: React.FC<LoanCardProps> = ({ loan }) => {
  const navigate = useNavigate();
  const productLabel = loan.productName || loan.loanProductName;
  const nameProduct = loan.name && productLabel ? `${loan.name} - ${productLabel}` : 'N/A';
  const loanAgreementNumber = loan.loanAgreementNumber || 'N/A';
  const isRestricted = loan.restrictAccessToPortal === true;

  return (
    <Card sx={{ borderRadius: 3, mb: 3, boxShadow: 3, background: (theme) => theme.palette.mode === 'dark' ? '#181818' : theme.palette.background.paper }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 4, p: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {isRestricted ? (
            <Box sx={{ minHeight: { xs: 80, sm: 110 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <LoanId
                variant="body1"
                sx={{
                  textAlign: 'center',
                  marginRight: 0,
                }}
              >
                Please contact the Momenta Finance team for more information
              </LoanId>
            </Box>
          ) : (
            <>
          {/* Top Row: Name/ID left, Status badge right */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LoanId variant="body1">{nameProduct}</LoanId>
              <AccountName variant="body1">({loan.accountName || 'N/A'})</AccountName>
            </Box>
            <LoanStatusChip label={loan.status || 'N/A'} status={loan.status} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap', my: 1 }}>
            <LoanAmount>{formatCurrency(loan.loanAmount)}</LoanAmount>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title={fieldMeta.numberOfInstallments.infoText}>
                <InfoOutlinedIcon fontSize="small" sx={{ color: '#bdbdbd' }} />
              </Tooltip>
              <Typography
                component="span"
                sx={{
                  fontFamily: "'Libre Franklin', sans-serif",
                  fontWeight: 400,
                  fontSize: 16,
                  color: BRAND_TABLE_TEXT_COLOR,
                }}
              >
                {fieldMeta.numberOfInstallments.label}: {loan.numberOfInstallments || '-'}
              </Typography>
            </Box>
          </Box>
          {/* Bottom Row: Data left, Button right */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mt: 1 }}>
            <Grid container spacing={2} sx={{ flex: 1 }}>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: BRAND_TABLE_TEXT_COLOR }}>
                  <span style={{ color: BRAND_TABLE_TEXT_COLOR }}>{fieldMeta.amountDueTillCurrent.label}</span>
                  <Tooltip title={fieldMeta.amountDueTillCurrent.infoText}><InfoOutlinedIcon fontSize="small" sx={{ color: '#bdbdbd' }} /></Tooltip>
                  <Box sx={{ fontWeight: 700, fontSize: 16, color: BRAND_TABLE_TEXT_COLOR, ml: 1 }}>
                    {formatCurrency(loan.amountDueTillCurrent)}
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: BRAND_TABLE_TEXT_COLOR }}>
                  <span style={{ color: BRAND_TABLE_TEXT_COLOR }}>{fieldMeta.nextInstallmentDate.label}</span>
                  <Tooltip title={fieldMeta.nextInstallmentDate.infoText}><InfoOutlinedIcon fontSize="small" sx={{ color: '#bdbdbd' }} /></Tooltip>
                  <Box sx={{ fontWeight: 700, fontSize: 16, color: BRAND_TABLE_TEXT_COLOR, ml: 1 }}>
                    {loan.nextInstallmentDate ? formatDateToDDMMYYYY(loan.nextInstallmentDate) : '-'}
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: BRAND_TABLE_TEXT_COLOR }}>
                  <span style={{ color: BRAND_TABLE_TEXT_COLOR }}>{fieldMeta.remainingTermMonths.label}</span>
                  <Tooltip title={fieldMeta.remainingTermMonths.infoText}><InfoOutlinedIcon fontSize="small" sx={{ color: '#bdbdbd' }} /></Tooltip>
                  <Box sx={{ fontWeight: 700, fontSize: 16, color: BRAND_TABLE_TEXT_COLOR, ml: 1 }}>
                    {loan.remainingTermMonths ?? '-'}
                  </Box>
                </Box>
              </Grid>
            </Grid>
            <ViewDetailsButton
              variant="outlined"
              aria-label={`View details for loan ${loan.name}`}
              onClick={() => navigate(`/loans/${loan.id}`)}
            >
              View Details
            </ViewDetailsButton>
          </Box>
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default LoanCard; 
